/**
 * What we have asked this supplier for, what came back, and what nobody has read a date off.
 *
 * THREE ANSWERS ABOUT EXPIRY, NOT TWO, AND THE PANE COUNTS THEM SEPARATELY. A certificate that has
 * expired is a problem. A certificate whose expiry NOBODY HAS EXTRACTED is not the same thing: it
 * is unknown, and the person who has to act on it acts differently -- one supplier needs chasing,
 * the other needs somebody to open a PDF. Collapsing the two produces a false CLEAR, which is the
 * direction that hurts, so `ExpiryUnknown` gets a column of its own and is never folded into
 * either neighbour.
 *
 * NOTHING ABOUT EXPIRY IS STORED. Expired, DaysUntilExpiry and every count are today's date against
 * a date on the paper, computed by the service on every read. A stored flag would be wrong every
 * night at midnight.
 *
 * CREATING A REQUEST IS NOT SENDING ONE, and the pane keeps them visibly apart. A draft reaches
 * nobody; sending puts a message from this company into an outside party's inbox, holds its own
 * privilege (`supplierDocument:send`), and cannot be recalled by any amount of permission. The
 * send is a separate button on a drafted row rather than a parameter on the create.
 *
 * A REQUEST THAT NAMES NOBODY IS EMAIL-ONLY AND INVISIBLE IN THE PORTAL BY DESIGN. That is one
 * column -- the external user it was sent to -- and the pane says which rows the supplier can
 * actually see, because "sent" and "they can see it" are different facts and only one of them
 * survives a changed email address.
 *
 * A READING BY A MODEL IS A CLAIM UNTIL SOMEBODY CHECKS IT. An extracted expiry date decides
 * whether a supplier is treated as qualified, so the pane marks which readings are still
 * attributed to a model and unconfirmed -- the same count the report carries, for the same reason.
 *
 * READ-ONLY FOR NOW, SAID PLAINLY. Drafting a request, recording an arrival and confirming an
 * extraction each have their own refusals and their own dialogs; this panel is the chase list and
 * the expiry report, which had no surface at all.
 */
import React, {useCallback, useContext, useEffect, useState} from "react";
import {Box, Chip, CircularProgress, Tooltip, Typography} from "@mui/material";

import {AppContext} from "../../util/AppContext";
import {makeCresAxios} from "../../util/Axios";
import {Grey2, Grey5, Grey7, Grey8, Grey17} from "../../theme/styles";

type Request_ = {
    USID: string; RequestType?: string | null; Subject?: string | null;
    SentToAddress?: string | null; SentToName?: string | null; Status?: string | null;
    DueDate?: string | null; SentDatetime?: string | null; Overdue?: boolean;
    VisibleInPortal?: boolean; Notes?: string | null;
};

type Received = {
    USID: string; DocumentType?: string | null; ReceivedDatetime?: string | null;
    CertificateNumber?: string | null; IssuedBy?: string | null; Standard?: string | null;
    IssuedDate?: string | null; ExpiryDate?: string | null;
    ExpiryKnown?: boolean; Expired?: boolean | null; DaysUntilExpiry?: number | null;
    ExtractedByModel?: string | null; ExtractionConfirmed?: boolean;
};

type Status_ = {
    Count?: number; Expired?: number; Current?: number; ExpiryUnknown?: number;
    ExpiringWithinDays?: number | null; ExpiringSoon?: number | null;
    AttributedToAModel?: number;
    ListOfSupplierReceivedDocument?: {SupplierReceivedDocument?: Received[] | Received};
};

function listOf<T>(held: T[] | T | undefined | null): T[] {
    if (held === null || held === undefined) return [];
    return Array.isArray(held) ? held : [held];
}

function when(value?: string | null): string {
    if (!value) return "—";
    const at = new Date(`${String(value).slice(0, 10)}T00:00:00`);
    if (Number.isNaN(at.getTime())) return String(value).slice(0, 10);
    return at.toLocaleDateString(undefined, {day: "numeric", month: "short", year: "numeric"});
}

function spell(value?: string | null): string {
    const text = String(value ?? "").replace(/_/g, " ").toLowerCase();
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : "—";
}

export default function SupplierDocumentsPane(
    {objectUSID}: {objectUSID?: string | null}) {

    const appContext: any = useContext(AppContext);
    const api = makeCresAxios(appContext?.sessionContext);

    const [status, setStatus] = useState<Status_ | null>(null);
    const [requests, setRequests] = useState<Request_[]>([]);
    const [overdue, setOverdue] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(() => {
        if (!objectUSID) return;
        setLoading(true);
        Promise.all([
            api.get("t1/v1/supplier_document_status",
                    {params: {SupplierObjectUSID: objectUSID, WithinDays: 60}}),
            api.get("t1/v1/supplier_document_requests",
                    {params: {SupplierObjectUSID: objectUSID}}),
        ])
            .then(([report, chase]: any[]) => {
                setStatus(report?.data?.PlatformMessage ?? null);
                const held = chase?.data?.PlatformMessage
                    ?.ListOfSupplierDocumentRequest?.SupplierDocumentRequest;
                setRequests(listOf<Request_>(held));
                setOverdue(chase?.data?.PlatformMessage?.Overdue ?? 0);
                setError(null);
            })
            .catch((problem: any) => {
                setStatus(null);
                setError(problem?.response?.data?.Cause
                         ?? "This supplier's documents could not be read.");
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [objectUSID]);

    useEffect(() => { load(); }, [load]);

    if (loading && !status && !error) {
        return <Box sx={{display: "flex", gap: "10px", alignItems: "center", padding: "20px 0"}}>
            <CircularProgress size={18}/>
            <Typography sx={{fontSize: 13, color: Grey7}}>Reading the paperwork…</Typography>
        </Box>;
    }

    if (error) {
        return <Typography sx={{fontSize: 13, color: "#B3261E", lineHeight: 1.6, maxWidth: 760,
                                paddingTop: "8px"}}>{error}</Typography>;
    }

    const documents = listOf<Received>(status?.ListOfSupplierReceivedDocument
                                       ?.SupplierReceivedDocument);
    const unknown = status?.ExpiryUnknown ?? 0;

    return (
        <Box sx={{paddingTop: "6px", maxWidth: 1020}}>

            {/* ── the three answers, counted separately ─────────────────────────────────────── */}
            <Box sx={{display: "flex", gap: "32px", flexWrap: "wrap",
                      border: `1px solid ${Grey2}`, borderRadius: "6px",
                      padding: "12px 16px", marginBottom: "10px"}}>
                <Count label="On file" value={status?.Count ?? 0} colour={Grey17}/>
                <Count label="Current" value={status?.Current ?? 0} colour="#1E7B34"/>
                <Count label="Expired" value={status?.Expired ?? 0}
                       colour={status?.Expired ? "#B3261E" : Grey17}/>
                {/* THE THIRD ANSWER, AND IT IS NOT A SHADE OF EITHER NEIGHBOUR. */}
                <Count label="Expiry unknown" value={unknown}
                       colour={unknown ? "#B26A00" : Grey17}/>
                {status?.ExpiringWithinDays != null && (
                    <Count label={`Expiring in ${status.ExpiringWithinDays}d`}
                           value={status.ExpiringSoon ?? 0}
                           colour={status.ExpiringSoon ? "#B26A00" : Grey17}/>
                )}
            </Box>

            {!!unknown && (
                <Typography sx={{fontSize: 13, color: "#B26A00", lineHeight: 1.6, maxWidth: 820,
                                 marginBottom: "16px"}}>
                    {unknown === 1
                        ? "One document has no expiry date read off it, so it is neither current "
                          + "nor expired"
                        : `${unknown} documents have no expiry date read off them, so they are `
                          + "neither current nor expired"}
                    {" "}— they are unknown. That is a different job from chasing a lapsed
                    certificate: somebody has to open the file and read it.
                </Typography>
            )}

            {!!status?.AttributedToAModel && (
                <Typography sx={{fontSize: 13, color: "#B26A00", lineHeight: 1.6, maxWidth: 820,
                                 marginBottom: "16px"}}>
                    {status.AttributedToAModel === 1
                        ? "One reading here was made by a model and nobody has confirmed it."
                        : `${status.AttributedToAModel} readings here were made by a model and `
                          + "nobody has confirmed them."}
                    {" "}A date read out of a PDF decides whether this supplier is treated as
                    qualified, so until somebody checks it, it is a claim rather than a fact.
                </Typography>
            )}

            {/* ── what we have asked for ────────────────────────────────────────────────────── */}
            <Box sx={{display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px"}}>
                <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8}}>
                    Asked for
                </Typography>
                {!!overdue && (
                    <Typography sx={{fontSize: 13, color: "#B3261E"}}>
                        {overdue === 1 ? "1 overdue" : `${overdue} overdue`}
                    </Typography>
                )}
            </Box>

            {requests.length === 0 ? (
                <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6, maxWidth: 820,
                                 marginBottom: "18px"}}>
                    Nothing has been asked of this supplier. A request is drafted first and sent as
                    a separate act — a draft reaches nobody.
                </Typography>
            ) : (
                <Box sx={{border: `1px solid ${Grey2}`, borderRadius: "6px", overflow: "hidden",
                          marginBottom: "18px"}}>
                    {requests.map(one => (
                        <Box key={one.USID}
                             sx={{padding: "10px 14px", borderBottom: `1px solid ${Grey2}`,
                                  "&:last-of-type": {borderBottom: "none"}}}>
                            <Box sx={{display: "flex", gap: "10px", alignItems: "baseline",
                                      flexWrap: "wrap"}}>
                                <Typography sx={{fontSize: 13, color: Grey17, fontWeight: 600,
                                                 flex: 1}}>
                                    {one.Subject || one.RequestType || "—"}
                                </Typography>
                                <Chip size="small" label={spell(one.Status)}
                                      sx={{height: 20, fontSize: 11}}/>
                                <Typography sx={{fontSize: 12,
                                                 color: one.Overdue ? "#B3261E" : Grey5,
                                                 minWidth: 120, textAlign: "right"}}>
                                    {one.DueDate ? `due ${when(one.DueDate)}` : "no due date"}
                                    {one.Overdue ? " — overdue" : ""}
                                </Typography>
                            </Box>
                            <Box sx={{display: "flex", gap: "14px", alignItems: "baseline",
                                      marginTop: "4px", flexWrap: "wrap"}}>
                                <Typography sx={{fontSize: 12, color: Grey5}}>
                                    {one.SentToName ? `${one.SentToName} — ` : ""}
                                    {one.SentToAddress}
                                </Typography>
                                {/* "SENT" AND "THEY CAN SEE IT" ARE DIFFERENT FACTS. A request
                                    naming no portal contact is email-only, which survives nothing
                                    -- not a changed address, not a bounced message. */}
                                <Typography sx={{fontSize: 12,
                                                 color: one.VisibleInPortal ? "#1E7B34" : Grey5}}>
                                    {one.VisibleInPortal
                                        ? "visible in their portal"
                                        : "email only — not visible in their portal"}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}

            {/* ── what came back ────────────────────────────────────────────────────────────── */}
            <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8, marginBottom: "8px"}}>
                On file
            </Typography>
            {documents.length === 0 ? (
                <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6, maxWidth: 820}}>
                    No document has been recorded against this supplier.
                </Typography>
            ) : (
                <Box sx={{border: `1px solid ${Grey2}`, borderRadius: "6px", overflow: "hidden"}}>
                    {documents.map(one => (
                        <Box key={one.USID}
                             sx={{padding: "10px 14px", borderBottom: `1px solid ${Grey2}`,
                                  "&:last-of-type": {borderBottom: "none"}}}>
                            <Box sx={{display: "flex", gap: "10px", alignItems: "baseline",
                                      flexWrap: "wrap"}}>
                                <Typography sx={{fontSize: 13, color: Grey17, fontWeight: 600,
                                                 flex: 1}}>
                                    {one.DocumentType || "—"}
                                    {one.CertificateNumber ? ` — ${one.CertificateNumber}` : ""}
                                </Typography>
                                {/* THREE STATES ON THE ROW TOO, not just in the counts. */}
                                {one.ExpiryKnown === false ? (
                                    <Typography sx={{fontSize: 13, color: "#B26A00",
                                                     minWidth: 170, textAlign: "right"}}>
                                        no expiry read off it
                                    </Typography>
                                ) : one.Expired ? (
                                    <Typography sx={{fontSize: 13, color: "#B3261E",
                                                     minWidth: 170, textAlign: "right"}}>
                                        expired {when(one.ExpiryDate)}
                                    </Typography>
                                ) : (
                                    <Typography sx={{fontSize: 13, color: "#1E7B34",
                                                     minWidth: 170, textAlign: "right"}}>
                                        expires {when(one.ExpiryDate)}
                                        {one.DaysUntilExpiry != null
                                            ? ` (${one.DaysUntilExpiry}d)` : ""}
                                    </Typography>
                                )}
                            </Box>
                            <Box sx={{display: "flex", gap: "14px", alignItems: "baseline",
                                      marginTop: "4px", flexWrap: "wrap"}}>
                                {one.IssuedBy && (
                                    <Typography sx={{fontSize: 12, color: Grey5}}>
                                        {one.IssuedBy}
                                    </Typography>
                                )}
                                {one.Standard && (
                                    <Typography sx={{fontSize: 12, color: Grey5}}>
                                        {one.Standard}
                                    </Typography>
                                )}
                                <Typography sx={{fontSize: 12, color: Grey5}}>
                                    received {when(one.ReceivedDatetime)}
                                </Typography>
                                {/* A READING IS A CLAIM UNTIL SOMEBODY CHECKS IT. */}
                                {one.ExtractedByModel && (
                                    <Tooltip title={one.ExtractionConfirmed
                                        ? "A person has confirmed these fields."
                                        : "Read by a model and not yet confirmed by a person."}>
                                        <Typography sx={{fontSize: 12,
                                                         color: one.ExtractionConfirmed
                                                                ? Grey5 : "#B26A00"}}>
                                            {one.ExtractionConfirmed
                                                ? `read by ${one.ExtractedByModel}, confirmed`
                                                : `read by ${one.ExtractedByModel}, unconfirmed`}
                                        </Typography>
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}

            <Typography sx={{fontSize: 12, color: Grey5, lineHeight: 1.6, marginTop: "16px",
                             maxWidth: 820}}>
                Expiry, lateness and every count here are today's date against a date on the paper,
                worked out when the page is read — none of it is stored. Drafting a request,
                recording an arrival and confirming a reading are not on this screen yet.
            </Typography>
        </Box>
    );
}

function Count({label, value, colour}: {label: string; value: number; colour: string}) {
    return (
        <Box>
            <Typography sx={{fontSize: 11, color: Grey5, textTransform: "uppercase",
                             letterSpacing: "0.04em"}}>
                {label}
            </Typography>
            <Typography sx={{fontSize: 20, color: colour, marginTop: "2px", fontWeight: 600}}>
                {value}
            </Typography>
        </Box>
    );
}
